import { modalIds } from '@const/modals';
import { apiAuthService, apiUserService, apiApiKeysService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useUserContext } from '@hooks/context/useUserContext';
import useModalContext from '@hooks/context/useModalContext';
import {
    ETwoFAMethod,
    TEmail,
    TNewPhone,
    TPhone,
    TApiKeyResponse,
    TCreateApiKeys
} from '@xeppt/xeppt-sdk/types';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useState } from 'react';

export const useSettingsModals = () => {
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const { refetchUser, user } = useUserContext();
    const { onOpen, onClose, handleChangeLoading, changeModalData } = useModalContext();
    const [authenticatorCode, setAuthenticatorCode] = useState<string>();

    const { handleRequest: handleVerifyMFA } = useApiMutation({
        method: (data: { token: string; code: string }) => {
            return apiAuthService.confirmTwoFA(data);
        },
        onSuccess: (data) => {
            if (data) {
                setAuthenticatorCode(data);
            }
            refetchUser();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDisableMFARequest } = useApiMutation({
        method: (type: ETwoFAMethod) => {
            handleChangeLoading(true);
            return apiAuthService.disableTwoFA();
        },
        onSuccess: (data, request) => {
            if (data) {
                handleConfirmDisableMFA(request, data.token);
            } else {
                refetchUser();
                onClose();
            }
        },
        onFinish: () => handleChangeLoading(false),
        onError: requestErrorLocale
    });

    const { handleRequest: handleResetOTPRequest } = useApiMutation({
        method: (code: string) => {
            return apiAuthService.resetTwoFA(code);
        },
        onSuccess: () => {
            refetchUser();
            onClose();
        },
        onError: requestErrorLocale
    });

    const handleChangeAvatar = () => {
        changeModalData({ avatar: user?.profile?.avatar });
        onOpen({
            modalId: modalIds.SETTINGS_UPLOAD_IMAGE,
            onSubmit: (data: { avatar: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .updateAvatar(data.avatar)
                    .then(() => {
                        refetchUser();
                        onClose();
                        requestSuccessLocale('update_avatar');
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeleteAvatar = () => {
        apiUserService
            .deleteAvatar()
            .then(() => {
                refetchUser();
                requestSuccessLocale('delete_avatar');
                onClose();
            })
            .catch(requestErrorLocale);
    };

    const handleOpenChangePasswordModal = () => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                onOpen({
                    modalId: modalIds.SETTINGS_CHANGE_PASSWORD,
                    onSubmit: ({
                        oldPassword,
                        newPassword
                    }: {
                        oldPassword: string;
                        newPassword: string;
                    }) => {
                        handleChangeLoading(true);
                        apiUserService
                            .changePassword(oldPassword, newPassword, {
                                id: data.secure_id,
                                code: data.code
                            })
                            .then(() => {
                                requestSuccessLocale('change_password');
                                onClose();
                            })
                            .catch((e) => {
                                requestErrorLocale(e);
                                changeModalData({ ...data, code: '' });
                            })
                            .finally(() => handleChangeLoading(false));
                    }
                });
            }
        });
    };

    const handleOpenAddPhoneModal = (refetchPhones: () => void) => {
        onOpen({
            modalId: modalIds.SETTINGS_ADD_PHONE_MODAL,
            onSubmit: (data: TNewPhone) => {
                handleChangeLoading(true);
                apiUserService
                    .addPhone(data)
                    .then(({ id }) => {
                        refetchPhones();
                        changeModalData({ ...data, phoneId: id });
                        onOpen({
                            modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
                            onSubmit: (data: { phoneId: string; code: string }) => {
                                handleChangeLoading(true);
                                apiUserService
                                    .verifyPhone(data.phoneId, data?.code as string)
                                    .then(() => {
                                        requestSuccessLocale('verify_phone');
                                        onClose();
                                        refetchPhones();
                                    })
                                    .catch((e) => {
                                        requestErrorLocale(e);
                                        changeModalData({ ...data, code: '' });
                                    })
                                    .finally(() => handleChangeLoading(false));
                            }
                        });
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleOpenAddEmailModal = (refetchEmails: () => void) => {
        onOpen({
            modalId: modalIds.SETTINGS_ADD_EMAIL_MODAL,
            onSubmit: (data: { email: string }) => {
                if (data?.email) {
                    handleChangeLoading(true);
                    apiUserService
                        .addEmail(data.email as string)
                        .then(({ id }) => {
                            refetchEmails();
                            changeModalData({ ...data, emailId: id, is_primary: false });
                            requestSuccessLocale('send_verification_email');
                            onOpen({
                                modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
                                onSubmit: (data: { emailId: string; code: string }) => {
                                    handleChangeLoading(true);
                                    apiUserService
                                        .verifyEMAIL(data.emailId, data?.code as string)
                                        .then(() => {
                                            requestSuccessLocale('verify_email');
                                            onClose();
                                            refetchEmails();
                                        })
                                        .catch((e) => {
                                            requestErrorLocale(e);
                                            changeModalData({ ...data, code: '' });
                                        })
                                        .finally(() => handleChangeLoading(false));
                                }
                            });
                        })
                        .catch(requestErrorLocale)
                        .finally(() => handleChangeLoading(false));
                }
            }
        });
    };

    const handleSetPrimaryEmail = (email: TEmail, refetchEmails: () => void) => {
        changeModalData({
            email: email.email,
            emailId: email.id,
            is_primary: true,
            is_primary_set: true
        });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .setPrimaryEmail(email.id, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('set_primary_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleSetPrimaryPhone = (phone: TPhone, refetchPhones: () => void) => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .setPrimaryPhone(phone.id, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('set_primary_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleVerifyPhone = (phone: TPhone, refetchPhones: () => void) => {
        changeModalData({
            phoneId: phone.id,
            phone_number: phone.phone_number,
            phone_code: phone.phone_code
        });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
            onSubmit: (data: { phoneId: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .verifyPhone(data.phoneId, data?.code as string)
                    .then(() => {
                        requestSuccessLocale('verify_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleVerifyEmail = (email: TEmail, refetchEmails: () => void) => {
        changeModalData({ ...email, emailId: email.id, isPrimary: false });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
            onSubmit: (data: { emailId: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .verifyEMAIL(data.emailId, data?.code as string)
                    .then(() => {
                        requestSuccessLocale('verify_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeletePhone = (data: TPhone, refetchPhones: () => void) => {
        changeModalData({
            phone_code: data.phone_code,
            phone_number: data.phone_number
        });
        onOpen({
            modalId: modalIds.DELETE_PHONE,
            onSubmit: () => {
                handleChangeLoading(true);
                apiUserService
                    .deletePhone(data.id)
                    .then(() => {
                        requestSuccessLocale('delete_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeleteEmail = (data: TEmail, refetchEmails: () => void) => {
        changeModalData({ email: data.email });
        onOpen({
            modalId: modalIds.DELETE_EMAIL,
            onSubmit: () => {
                handleChangeLoading(true);
                apiUserService
                    .deleteEmail(data.id)
                    .then(() => {
                        requestSuccessLocale('delete_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleChangePassword = (
        oldPassword: string,
        newPassword: string,
        onSuccess: () => void
    ) => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .changePassword(oldPassword, newPassword, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('password_changed');
                        onSuccess();
                        onClose();
                        refetchUser();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleEnableMFA = (type: ETwoFAMethod, onSubmit: () => void) => {
        changeModalData({
            type
        });
        onOpen({
            modalId: modalIds.ENABLE_MFA,
            onSubmit
        });
    };

    const handleConfirmDisableMFA = (type: ETwoFAMethod, token: string) => {
        changeModalData({
            type,
            isCodeSent: true
        });
        onOpen({
            modalId: modalIds.DISABLE_MFA,
            onSubmit: (data: { mfaCode: string; isRecoveryCode: boolean }) => {
                if (!data?.isRecoveryCode) {
                    handleVerifyMFA({ token, code: data?.mfaCode });
                } else {
                    handleResetOTPRequest(data?.mfaCode);
                }
            }
        });
    };

    const handleDisableMFA = (type: ETwoFAMethod) => {
        changeModalData({
            type
        });
        onOpen({
            modalId: modalIds.DISABLE_MFA,
            onSubmit: () => {
                handleDisableMFARequest(type);
            }
        });
    };

    const handleSetPhoneMFA = () => {
        changeModalData({
            phone_number: user?.phone_number,
            phone_code: user?.phone_code,
            customCode: true,
            is_primary: true
        });
        apiAuthService.setupTwoFA(ETwoFAMethod.SMS).then((mfaResult) => {
            onOpen({
                modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
                onSubmit: (data: { phoneId: string; code: string }) => {
                    handleChangeLoading(true);
                    handleVerifyMFA({ token: mfaResult.token, code: data?.code as string });
                }
            });
        });
    };

    const handleSetEmailMFA = () => {
        changeModalData({
            email: user?.email,
            customCode: true,
            is_primary: true
        });
        apiAuthService.setupTwoFA(ETwoFAMethod.EMAIL).then((mfaResult) => {
            onOpen({
                modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
                onSubmit: (data: { phoneId: string; code: string }) => {
                    handleChangeLoading(true);
                    handleVerifyMFA({ token: mfaResult.token, code: data?.code as string });
                }
            });
        });
    };

    const handleDeleteApiKey = (onSuccess: () => void) => {
        onOpen({
            modalId: modalIds.DELETE_API_KEYS,
            onSubmit: () => {
                handleChangeLoading(true);
                apiApiKeysService
                    .deleteApiKeys()
                    .then(() => {
                        requestSuccessLocale('delete_api_key');
                        onClose();
                        onSuccess();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleCreateApiKey = (onSuccess: () => void, apiKeys?: TApiKeyResponse) => {
        const handleCreateApiKeyRequest = () => {
            handleChangeLoading(true);
            apiApiKeysService
                .createApiKeys({})
                .then((data) => {
                    requestSuccessLocale('create_api_key');
                    onSuccess();
                    changeModalData(data);
                    onOpen({
                        modalId: modalIds.CREATE_API_KEYS,
                        onSubmit: onClose
                    });
                })
                .catch(requestErrorLocale)
                .finally(() => handleChangeLoading(false));
        };
        if (apiKeys) {
            onOpen({
                modalId: modalIds.API_KEY_INFO,
                onSubmit: handleCreateApiKeyRequest
            });
        } else {
            handleCreateApiKeyRequest();
        }
    };

    const handleAddWebhooks = (onSuccess: () => void) => {
        changeModalData({ isIUpdate: false, order_webhook: '', refund_webhook: '' });
        onOpen({
            modalId: modalIds.CREATE_UPDATE_API_KEY_WEBHOOK,
            onSubmit: (data: TApiKeyResponse) => {
                handleChangeLoading(true);
                apiApiKeysService
                    .updateApiKeys({
                        order_webhook: data?.order_webhook,
                        refund_webhook: data?.refund_webhook
                    })
                    .then(() => {
                        requestSuccessLocale('add_webhooks');
                        onClose();
                        onSuccess();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleUpdateWebhooks = (apiKeys: TApiKeyResponse, onSuccess: () => void) => {
        changeModalData({ isIUpdate: true, ...apiKeys });
        onOpen({
            modalId: modalIds.CREATE_UPDATE_API_KEY_WEBHOOK,
            onSubmit: (data: TApiKeyResponse) => {
                handleChangeLoading(true);
                apiApiKeysService
                    .updateApiKeys({
                        order_webhook: data?.order_webhook,
                        refund_webhook: data?.refund_webhook
                    })
                    .then(() => {
                        requestSuccessLocale('update_webhooks');
                        onClose();
                        onSuccess();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeleteWebhook = (
        apiKeys: TApiKeyResponse,
        remove: 'order_webhook' | 'refund_webhook',
        onSuccess: () => void
    ) => {
        onOpen({
            modalId: modalIds.DELETE_API_KEY_WEBHOOK,
            onSubmit: () => {
                handleChangeLoading(true);
                apiApiKeysService
                    .updateApiKeys({
                        order_webhook: remove === 'order_webhook' ? '' : apiKeys.order_webhook,
                        refund_webhook: remove === 'refund_webhook' ? '' : apiKeys.refund_webhook
                    })
                    .then(() => {
                        requestSuccessLocale('delete_webhook');
                        onClose();
                        onSuccess();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    return {
        handleChangeAvatar,
        handleDeleteAvatar,
        handleOpenChangePasswordModal,
        handleOpenAddPhoneModal,
        handleOpenAddEmailModal,
        handleSetPrimaryPhone,
        handleDeletePhone,
        handleSetPrimaryEmail,
        handleDeleteEmail,
        handleVerifyPhone,
        handleVerifyEmail,
        handleChangePassword,
        handleSetEmailMFA,
        handleSetPhoneMFA,
        handleEnableMFA,
        authenticatorCode,
        handleDisableMFA,
        handleCreateApiKey,
        handleDeleteApiKey,
        handleDeleteWebhook,
        handleAddWebhooks,
        handleUpdateWebhooks
    };
};
